<template>
  <div class="issuance-production-data-table">
    <!-- Table Controls -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="d-flex gap-2 align-items-center">
        <div class="filter-group">
          <div class="d-flex gap-2">
            <select
              v-model="filters.status"
              class="form-select form-select-sm"
              @change="applyFilters"
            >
              <option value="">
                {{ $t("issuance_reporting.filters.all_statuses") }}
              </option>
              <option value="pending">
                {{ $t("issuance_reporting.status.pending") }}
              </option>
              <option value="accepted">
                {{ $t("issuance_reporting.status.accepted") }}
              </option>
              <option value="rejected">
                {{ $t("issuance_reporting.status.rejected") }}
              </option>
              <option value="completed">
                {{ $t("issuance_reporting.status.completed") }}
              </option>
            </select>

            <select
              v-model="filters.deviceId"
              class="form-select form-select-sm"
              @change="applyFilters"
            >
              <option value="">
                {{ $t("issuance_reporting.filters.all_devices") }}
              </option>
              <option
                v-for="device in registryDevices"
                :key="device.id"
                :value="device.id"
              >
                {{ device.device_name }} ({{ device.device_eic }})
              </option>
            </select>
          </div>
        </div>

        <div class="period-filter">
          <div class="d-flex gap-1">
            <MonthPickerComponent
              v-model="filters.periodStart"
              :placeholder="$t('issuance_reporting.filters.period_start')"
              :ariaLabel="$t('issuance_reporting.filters.period_start')"
              clearable
              @update:modelValue="applyFilters"
            />
            <span class="align-self-center">-</span>
            <MonthPickerComponent
              v-model="filters.periodEnd"
              :placeholder="$t('issuance_reporting.filters.period_end')"
              :ariaLabel="$t('issuance_reporting.filters.period_end')"
              clearable
              @update:modelValue="applyFilters"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <LoadingIndicatorComponent v-if="loading" />

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
      <button class="btn btn-outline-danger btn-sm ms-2" @click="refreshData">
        {{ $t("common.retry") }}
      </button>
    </div>

    <!-- Main Table -->
    <div v-else-if="mainRows.length > 0" class="table-responsive">
      <table class="table table-hover">
        <thead class="table-light">
          <tr>
            <th class="expand-column"></th>
            <th class="select-column">
              <font-awesome-icon
                icon="check-square"
                class="text-muted"
                title="Select records"
              />
            </th>
            <th>{{ $t("issuance_reporting.table.device") }}</th>
            <th>{{ $t("issuance_reporting.table.device_code") }}</th>
            <th>{{ $t("issuance_reporting.table.total_production") }}</th>
            <th>{{ $t("issuance_reporting.table.date") }}</th>
            <th>{{ $t("issuance_reporting.table.actions") }}</th>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="mainRow in mainRows"
            :key="mainRow.registry_device_id"
          >
            <!-- Device Header Row -->
            <tr
              class="device-row"
              :class="{
                'table-active': expandedDevices.has(mainRow.registry_device_id),
              }"
            >
              <td>
                <button
                  class="btn btn-sm btn-link p-0"
                  @click="toggleDevice(mainRow.registry_device_id)"
                >
                  <font-awesome-icon
                    :icon="
                      expandedDevices.has(mainRow.registry_device_id)
                        ? 'chevron-down'
                        : 'chevron-right'
                    "
                  />
                </button>
              </td>
              <td>
                <!-- Device Master Checkbox -->
                <input
                  type="checkbox"
                  :checked="
                    store.isDeviceFullySelected(mainRow.registry_device_id)
                  "
                  :indeterminate="
                    store.isDevicePartiallySelected(mainRow.registry_device_id)
                  "
                  @change="
                    store.toggleDeviceSelection(mainRow.registry_device_id)
                  "
                  class="form-check-input"
                  :disabled="mainRow.subrows.length === 0"
                />
              </td>
              <td>
                <strong>{{ getDeviceName(mainRow.registry_device_id) }}</strong>
              </td>
              <td>
                <code>{{ getDeviceEic(mainRow.registry_device_id) }}</code>
              </td>
              <td>
                <strong>{{ formatMWh(mainRow.total_value) }}</strong>
              </td>
              <td>
                {{ getDeviceDateRange(mainRow) }}
              </td>
              <td></td>
            </tr>

            <!-- Monthly Production Records (Expandable) -->
            <tr
              v-for="subrow in sortSubrows(mainRow.subrows)"
              v-show="expandedDevices.has(mainRow.registry_device_id)"
              :key="subrow.id"
              class="sub-row"
            >
              <td></td>
              <td>
                <!-- Individual Subrow Checkbox -->
                <input
                  type="checkbox"
                  :checked="store.selectedSubrowIds.has(subrow.id)"
                  @change="store.toggleSubrowSelection(subrow.id)"
                  class="form-check-input"
                />
              </td>
              <td></td>
              <td>
                <PlIssuanceStatusCell
                  v-if="props.company.country === 'PL'"
                  :country-metadata="subrow.country_metadata"
                  :can-edit="Boolean(props.isAdmin)"
                  @save="
                    (payload) => handleCountryMetadataUpdate(subrow, payload)
                  "
                />
                <span
                  v-else
                  class="badge"
                  :class="getStatusBadgeClass(subrow.status)"
                >
                  {{ $t(`issuance_reporting.status.${subrow.status}`) }}
                </span>
              </td>
              <td>
                <!-- VALUE CELL - Enhanced for inline editing -->
                <div v-if="!editingRecords.has(subrow.id)">
                  <span>{{ formatMWh(subrow.value) }}</span>
                </div>
                <div v-else>
                  <div class="position-relative">
                    <input
                      :value="editingValues.get(subrow.id)"
                      @input="
                        (e: Event) =>
                          editingValues.set(
                            subrow.id,
                            parseInt((e.target as HTMLInputElement).value) || 0
                          )
                      "
                      type="number"
                      step="1"
                      class="form-control form-control-sm"
                      :class="{
                        'is-invalid': $v[subrow.id]?.value?.$error,
                        'is-valid':
                          $v[subrow.id]?.value?.$dirty &&
                          !$v[subrow.id]?.value?.$error,
                      }"
                      @blur="$v[subrow.id]?.value?.$touch()"
                      style="min-width: 100px"
                    />
                    <div
                      v-if="$v[subrow.id]?.value?.$error"
                      class="invalid-feedback"
                    >
                      {{ $v[subrow.id].value.$errors[0]?.$message }}
                    </div>
                  </div>
                </div>
              </td>
              <td>
                {{ formatMonthlyPeriod(subrow) }}
              </td>
              <td>
                <!-- ACTIONS CELL -->
                <div v-if="!editingRecords.has(subrow.id)" class="btn-group">
                  <button
                    @click="startEdit(subrow)"
                    class="btn btn-sm btn-outline-primary"
                    title="Edit value"
                  >
                    <font-awesome-icon icon="edit" />
                  </button>
                  <button
                    @click="handleDeleteRecord(subrow)"
                    class="btn btn-sm btn-outline-danger"
                    title="Delete record"
                    :disabled="recordLoadingStates.has(subrow.id)"
                  >
                    <span
                      v-if="recordLoadingStates.has(subrow.id)"
                      class="spinner-border spinner-border-sm"
                    ></span>
                    <font-awesome-icon v-else icon="trash" />
                  </button>
                </div>
                <div v-else class="btn-group">
                  <button
                    @click="saveEdit(subrow)"
                    class="btn btn-sm btn-success"
                    :disabled="
                      $v[subrow.id]?.value?.$invalid ||
                      recordLoadingStates.has(subrow.id)
                    "
                    title="Save changes"
                  >
                    <span
                      v-if="recordLoadingStates.has(subrow.id)"
                      class="spinner-border spinner-border-sm me-1"
                    ></span>
                    <font-awesome-icon icon="check" />
                  </button>
                  <button
                    @click="cancelEdit(subrow)"
                    class="btn btn-sm btn-secondary"
                    title="Cancel editing"
                  >
                    <font-awesome-icon icon="times" />
                  </button>
                </div>
              </td>
            </tr>

            <!-- Show placeholder row if no subrows -->
            <tr
              v-if="mainRow.subrows.length === 0"
              v-show="expandedDevices.has(mainRow.registry_device_id)"
              class="sub-row text-muted"
            >
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <span class="text-muted">0 {{ $t("common.mwh") }}</span>
              </td>
              <td>
                <span class="text-muted">{{
                  $t("issuance_reporting.no_data_for_period")
                }}</span>
              </td>
              <td></td>
            </tr>

            <!-- Add new subrow functionality -->
            <tr
              v-if="!creatingRecord.has(mainRow.registry_device_id)"
              v-show="expandedDevices.has(mainRow.registry_device_id)"
              class="sub-row add-row"
            >
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <button
                  @click="startCreateRecord(mainRow.registry_device_id)"
                  class="btn btn-sm btn-outline-success"
                  :disabled="isAnyRecordBeingCreated"
                >
                  <font-awesome-icon icon="plus" class="me-1" />
                  {{ $t("issuance_reporting.add_new") }}
                </button>
              </td>
            </tr>

            <!-- Create new record row -->
            <tr
              v-if="creatingRecord.has(mainRow.registry_device_id)"
              v-show="expandedDevices.has(mainRow.registry_device_id)"
              class="sub-row creating-row"
            >
              <td></td>
              <td></td>
              <td></td>
              <td>
                <span class="badge bg-warning">{{
                  $t("issuance_reporting.status.pending")
                }}</span>
              </td>
              <td>
                <!-- Total Production Input -->
                <div class="d-flex align-items-center gap-2">
                  <div class="position-relative">
                    <input
                      v-model="newRecordData.totalProduction"
                      type="number"
                      step="1"
                      class="form-control form-control-sm"
                      :class="{
                        'is-invalid': $v.newRecord?.totalProduction?.$error,
                        'is-valid':
                          $v.newRecord?.totalProduction?.$dirty &&
                          !$v.newRecord?.totalProduction?.$error,
                      }"
                      style="min-width: 120px"
                      @blur="$v.newRecord?.totalProduction?.$touch()"
                      @input="$v.newRecord?.totalProduction?.$touch()"
                    />
                    <div
                      v-if="$v.newRecord?.totalProduction?.$error"
                      class="invalid-feedback"
                    >
                      {{ $v.newRecord.totalProduction.$errors[0]?.$message }}
                    </div>
                  </div>
                  <span class="text-muted">{{ $t("common.mwh") }}</span>
                </div>
              </td>
              <td>
                <!-- Date Input -->
                <div class="position-relative">
                  <MonthPickerComponent
                    v-model="newRecordData.date"
                    :max="currentMonth"
                    :invalid="Boolean($v.newRecord?.date?.$error)"
                    class="new-record-month"
                    @update:modelValue="$v.newRecord?.date?.$touch()"
                  />
                  <div
                    v-if="$v.newRecord?.date?.$error"
                    class="invalid-feedback d-block"
                  >
                    {{ $v.newRecord.date.$errors[0]?.$message }}
                  </div>
                </div>
              </td>
              <td>
                <!-- Actions for creating new record -->
                <div class="btn-group">
                  <button
                    @click="saveNewRecord(mainRow.registry_device_id)"
                    class="btn btn-sm btn-success"
                    :disabled="
                      $v.newRecord?.$invalid || recordLoadingStates.has(-1)
                    "
                  >
                    <span
                      v-if="recordLoadingStates.has(-1)"
                      class="spinner-border spinner-border-sm me-1"
                    ></span>
                    <font-awesome-icon icon="check" />
                  </button>
                  <button
                    @click="cancelCreateRecord(mainRow.registry_device_id)"
                    class="btn btn-sm btn-secondary"
                    :disabled="recordLoadingStates.has(-1)"
                  >
                    <font-awesome-icon icon="times" />
                  </button>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-5">
      <div class="mb-3">
        <font-awesome-icon icon="inbox" size="3x" class="text-muted" />
      </div>
      <h5 class="text-muted">{{ $t("issuance_reporting.no_data") }}</h5>
      <p class="text-muted">
        {{ $t("issuance_reporting.no_data_description") }}
      </p>
    </div>
  </div>

  <!-- Confirmation Modal -->
  <ConfirmationModalComponent
    :visible="showDeleteConfirmation"
    :title="$t('issuance_reporting.delete_record')"
    :message="$t('issuance_reporting.confirm_delete_record')"
    @confirm="confirmDelete"
    @cancel="cancelDelete"
  />
</template>

<script setup lang="ts">
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MonthPickerComponent from "@/components/common/MonthPickerComponent.vue";
import { formatMonthYear } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useValidators } from "@/composables/useValidators";
import PlIssuanceStatusCell from "@/dashboard/production_strategy/issuance-reporting/pl/PlIssuanceStatusCell.vue";
import {
  useIssuanceProductionDataStore,
  type IssuanceProductionDataCountryMetadata,
  type IssuanceProductionDataMainRow,
  type IssuanceProductionDataSubRow,
} from "@/dashboard/production_strategy/issuance-reporting/useIssuanceProductionDataStore";
import type { Company } from "@/types";
import { useVuelidate } from "@vuelidate/core";
import { format } from "date-fns";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  company: Company;
  isAdmin?: boolean;
}>();

const { t } = useI18n();
const store = useIssuanceProductionDataStore();
const { required, productionValue, notFutureDate } = useValidators(t);
// `notFutureDate` refuses a later month, so the picker does not offer one.
const currentMonth = format(new Date(), "yyyy-MM");
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

// Local state
const expandedDevices = ref(new Set<number>());
const filters = ref({
  status: "",
  deviceId: "",
  periodStart: "",
  periodEnd: "",
});

// Edit state management (simplified)
const editingRecords = ref(new Set<number>());
const editingValues = ref(new Map<number, number>());

// Create new record state
const creatingRecord = ref(new Set<number>());
const newRecordData = ref({
  totalProduction: null as number | null,
  date: "",
});

const isAnyRecordBeingCreated = computed(() => {
  return creatingRecord.value.size > 0;
});

// Deletion confirmation modal state
const showDeleteConfirmation = ref(false);
const pendingDeleteRecord = ref<IssuanceProductionDataSubRow | null>(null);

// Reactive refs from store
const { mainRows, registryDevices, loading, error, recordLoadingStates } =
  storeToRefs(store);

// Vuelidate setup
const validationRules = computed(() => {
  const rules: any = {};

  // Validation rules for editing existing records
  editingRecords.value.forEach((recordId) => {
    rules[recordId] = {
      value: { required, productionValue },
    };
  });

  // Validation rules for creating new records
  if (creatingRecord.value.size > 0) {
    rules.newRecord = {
      totalProduction: { required, productionValue },
      date: { required, notFutureDate },
    };
  }

  return rules;
});

const validationState = computed(() => {
  const state: any = {};

  // Validation state for editing existing records
  editingRecords.value.forEach((recordId) => {
    state[recordId] = {
      value: editingValues.value.get(recordId) || 0,
    };
  });

  // Validation state for creating new records
  if (creatingRecord.value.size > 0) {
    state.newRecord = {
      totalProduction: newRecordData.value.totalProduction,
      date: newRecordData.value.date,
    };
  }

  return state;
});

const $v = useVuelidate(validationRules, validationState);

// Device helper functions
const getDevice = (deviceId: number) => {
  // First try to get from mainRows (which includes registry_device data)
  const mainRow = mainRows.value.find(
    (row) => row.registry_device_id === deviceId
  );
  if (mainRow?.registry_device) {
    return mainRow.registry_device;
  }
  // Fallback to registryDevices from store
  return registryDevices.value.find((d) => d.id === deviceId);
};

const getDeviceName = (deviceId: number) => {
  return getDevice(deviceId)?.device_name || "Unknown Device";
};

const getDeviceEic = (deviceId: number) => {
  return getDevice(deviceId)?.device_eic || "N/A";
};

const getDeviceDateRange = (mainRow: IssuanceProductionDataMainRow) => {
  if (mainRow.subrows.length === 0) return "-";

  const dates = mainRow.subrows.map((r) => new Date(r.period_start_at));
  const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

  if (mainRow.subrows.length === 1) {
    return formatMonthYear(minDate.toISOString());
  }

  return `${formatMonthYear(minDate.toISOString())} - ${formatMonthYear(
    maxDate.toISOString()
  )}`;
};

const sortSubrows = (subrows: IssuanceProductionDataSubRow[]) => {
  return subrows.sort(
    (a, b) =>
      new Date(a.period_start_at).getTime() -
      new Date(b.period_start_at).getTime()
  );
};

// Formatting functions
const formatMWh = (value: number) => {
  return `${Math.floor(value)} ${t("common.mwh")}`;
};

const formatMonthlyPeriod = (subrow: IssuanceProductionDataSubRow) => {
  const start = new Date(subrow.period_start_at);

  return formatMonthYear(start.toISOString());
};

const getStatusBadgeClass = (status: string) => {
  const classes = {
    pending: "bg-warning",
    accepted: "bg-success",
    rejected: "bg-danger",
    completed: "bg-primary",
    initial: "bg-secondary",
  };
  return classes[status as keyof typeof classes] || "bg-secondary";
};

// Device expansion
const toggleDevice = (deviceId: number) => {
  if (expandedDevices.value.has(deviceId)) {
    expandedDevices.value.delete(deviceId);
  } else {
    expandedDevices.value.add(deviceId);
  }
};

// Filters
const applyFilters = async () => {
  const queryParams: any = {};

  if (filters.value.status) queryParams.status = filters.value.status;
  if (filters.value.deviceId)
    queryParams.registry_device_id = Number(filters.value.deviceId);
  if (filters.value.periodStart)
    queryParams.period_start = `${filters.value.periodStart}-01T00:00:00Z`;
  if (filters.value.periodEnd) {
    // Create end of month date: YYYY-MM format -> last day of month at 23:59:59.999Z
    const [year, month] = filters.value.periodEnd.split("-").map(Number);
    const endDate = new Date(year, month, 0, 23, 59, 59, 999); // month is 0-indexed, so this gets last day of specified month
    queryParams.period_end = endDate.toISOString();
  }

  if (props.company.id) {
    await store.fetchIssuanceProductionData(props.company.id, queryParams);
  }
};

const refreshData = async () => {
  if (props.company.id) {
    await store.fetchIssuanceProductionData(props.company.id);
  }
};

// Edit management functions with Vuelidate
const startEdit = (subrow: IssuanceProductionDataSubRow) => {
  editingValues.value.set(subrow.id, subrow.value);
  editingRecords.value.add(subrow.id);
};

const cancelEdit = (subrow: IssuanceProductionDataSubRow) => {
  editingRecords.value.delete(subrow.id);
  editingValues.value.delete(subrow.id);
  $v.value[subrow.id]?.$reset();
};

const saveEdit = async (subrow: IssuanceProductionDataSubRow) => {
  // Validate using Vuelidate
  await $v.value[subrow.id]?.$validate();
  if ($v.value[subrow.id]?.$invalid) return;

  const newValue = editingValues.value.get(subrow.id);
  if (newValue === undefined || newValue === subrow.value) {
    cancelEdit(subrow);
    return;
  }

  try {
    recordLoadingStates.value.add(subrow.id);

    if (props.company.id) {
      await store.updateIssuanceProductionRecord(props.company.id, subrow.id, {
        value: newValue,
      });
    }

    // Success - clear edit state
    editingRecords.value.delete(subrow.id);
    editingValues.value.delete(subrow.id);
    $v.value[subrow.id]?.$reset();

    // Show success feedback
    globalSuccessHandler(
      t("common.success"),
      t("issuance_reporting.update_success")
    );
  } catch (error: any) {
    // Handle error using global error handler
    globalErrorHandler(error);
  } finally {
    recordLoadingStates.value.delete(subrow.id);
  }
};

// Get current filters for export - using same format as applyFilters
const getCurrentFilters = () => {
  const queryParams: any = {};

  if (filters.value.status) queryParams.status = filters.value.status;
  if (filters.value.deviceId)
    queryParams.registry_device_id = Number(filters.value.deviceId);
  if (filters.value.periodStart)
    queryParams.period_start = `${filters.value.periodStart}-01T00:00:00Z`;
  if (filters.value.periodEnd) {
    // Create end of month date: YYYY-MM format -> last day of month at 23:59:59.999Z
    const [year, month] = filters.value.periodEnd.split("-").map(Number);
    const endDate = new Date(year, month, 0, 23, 59, 59, 999); // month is 0-indexed, so this gets last day of specified month
    queryParams.period_end = endDate.toISOString();
  }

  // Add selected record IDs if any are selected
  if (store.selectedCount > 0) {
    const selectedIds = Array.from(store.selectedSubrowIds);
    queryParams.record_ids = selectedIds.join(",");
  }

  return queryParams;
};

// Create new record methods
const startCreateRecord = (deviceId: number) => {
  // Reset form data
  newRecordData.value = {
    totalProduction: null,
    date: "",
  };

  creatingRecord.value.add(deviceId);

  // Expand the device if not already expanded
  if (!expandedDevices.value.has(deviceId)) {
    expandedDevices.value.add(deviceId);
  }
};

const cancelCreateRecord = (deviceId: number) => {
  creatingRecord.value.delete(deviceId);
  newRecordData.value = {
    totalProduction: null,
    date: "",
  };
  // Reset Vuelidate validation
  $v.value.newRecord?.$reset();
};

const saveNewRecord = async (deviceId: number) => {
  // Validate using Vuelidate
  await $v.value.newRecord?.$validate();
  if ($v.value.newRecord?.$invalid) return;

  // Validate company ID exists
  if (props.company.id == null) {
    globalErrorHandler(new Error(t("errors.missingCompanyId")));
    return;
  }

  try {
    recordLoadingStates.value.add(-1); // Use -1 for new record loading state

    await store.createIssuanceProductionRecord(
      props.company.id,
      deviceId,
      newRecordData.value.totalProduction ?? 0,
      newRecordData.value.date + "-01" // Convert YYYY-MM to YYYY-MM-01
    );

    // Success - clear create state
    creatingRecord.value.delete(deviceId);
    newRecordData.value = {
      totalProduction: null,
      date: "",
    };
    // Reset Vuelidate validation
    $v.value.newRecord?.$reset();

    // Show success feedback
    globalSuccessHandler(
      t("common.success"),
      t("issuance_reporting.create_success")
    );
  } catch (error: any) {
    // Handle error using global error handler
    globalErrorHandler(error);
  } finally {
    recordLoadingStates.value.delete(-1);
  }
};

const handleCountryMetadataUpdate = async (
  subrow: IssuanceProductionDataSubRow,
  payload: { country_metadata: IssuanceProductionDataCountryMetadata }
) => {
  if (!props.company.id) {
    globalErrorHandler(new Error(t("errors.missingCompanyId")));
    return;
  }

  try {
    recordLoadingStates.value.add(subrow.id);

    await store.updateIssuanceProductionRecord(props.company.id, subrow.id, {
      country_metadata: payload.country_metadata,
    });

    globalSuccessHandler(
      t("common.success"),
      t("issuance_reporting.update_success")
    );
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    recordLoadingStates.value.delete(subrow.id);
  }
};

const handleDeleteRecord = (subrow: IssuanceProductionDataSubRow) => {
  // Store the record to be deleted and show confirmation modal
  pendingDeleteRecord.value = subrow;
  showDeleteConfirmation.value = true;
};

const confirmDelete = async () => {
  if (!pendingDeleteRecord.value) return;

  // Validate company ID exists
  if (props.company.id == null) {
    globalErrorHandler(new Error(t("errors.missingCompanyId")));
    return;
  }

  try {
    recordLoadingStates.value.add(pendingDeleteRecord.value.id);

    await store.deleteIssuanceProductionRecord(
      props.company.id,
      pendingDeleteRecord.value.id
    );

    // Show success feedback
    globalSuccessHandler(
      t("common.success"),
      t("issuance_reporting.delete_success")
    );
  } catch (error: any) {
    // Handle error using global error handler
    globalErrorHandler(error);
  } finally {
    recordLoadingStates.value.delete(pendingDeleteRecord.value.id);
    // Close modal and reset state
    showDeleteConfirmation.value = false;
    pendingDeleteRecord.value = null;
  }
};

const cancelDelete = () => {
  // Close modal and reset state
  showDeleteConfirmation.value = false;
  pendingDeleteRecord.value = null;
};

// Expose methods for parent component
defineExpose({
  getCurrentFilters,
});

// Lifecycle
onMounted(async () => {
  await applyFilters();
});
</script>

<style scoped lang="scss">
.new-record-month {
  min-width: 140px;
}

.expand-column {
  width: 30px;
}

.select-column {
  width: 40px;
}

.table > :not(caption) > * > * {
  padding: 0.75rem 0.5rem;
}

.badge {
  font-size: 0.75em;
}

code {
  font-size: 0.875em;
  color: var(--bs-secondary);
}
</style>
